import { Component, EventEmitter, OnDestroy, OnInit, Output, inject } from '@angular/core';
import type { Subscription } from 'rxjs';
import { ExportEngine, type ExportColumn } from '@memberjunction/export-engine';
import { MJNotificationService } from '@memberjunction/ng-notifications';
import { MJConfirmService } from '@memberjunction/ng-ui-components';
import { NeutralizeFormulaCells } from 'agu_app_shell';
import { GateByKey } from 'agu_oep_core';
import { OEPActionsService, type OEPSendPurposeView, type OEPTallyView } from '../../data/oep-actions.service';
import { OEPViewBase } from '../../state/oep-view-base';
import type { OEPStatusLine } from '../shared/status';
import { OEPDownloadExport } from '../shared/download';
import {
    FailureLine, GateRows, GateToast, IsLive, Nudges, OEP_NO_NUDGE, OEP_PROGRAM_STATS_QUERY_ID,
    OEP_SECTION_CSV_COLUMNS, OEP_SECTION_STATUS_QUERY_ID, ParseSectionRow, ParseStatsRow, PreviewLine, RemindersEnabled, SectionCsvRows,
    SectionRows, StatTiles, TallyLine,
    type OEPGateRow, type OEPSectionRowVM, type OEPSectionStatusRow, type OEPStatTile,
} from './program-status.vm';

/**
 * AGU staff "Program status" — markup.html:843-905. Counts come from two saved MJ Queries run in one
 * RunQuery.RunQueries round trip (OEP Program Stats, OEP Section Status; MJ: Query Permissions grant
 * them to OEP Staff only). Every send and gate goes through OEPActionsService (MJ RunAction by pinned
 * Action ID); before any email goes out the server's PreviewSend result — mode and redirect address
 * as the server will apply them — is shown in an MJConfirmService dialog (a warning when any email is
 * live). A gate run reloads the context (the phase moves) and this view. The By section table exports
 * with ExportEngine.toCSV after NeutralizeFormulaCells (agu_app_shell; MJ's CSV exporter has no
 * formula guard — upstream defect #2).
 */
@Component({
    standalone: false,
    selector: 'oep-program-status',
    templateUrl: './oep-program-status.component.html',
    styleUrl: './oep-program-status.component.scss',
})
export class OEPProgramStatusComponent extends OEPViewBase implements OnInit, OnDestroy {
    private readonly confirm = inject(MJConfirmService);
    private readonly actions = inject(OEPActionsService);

    /** "Email templates" (markup.html:880): the surface opens its email overlay. */
    @Output() OpenEmails = new EventEmitter<void>();

    SeasonCode = '';
    Stats: OEPStatTile[] = [];
    Gates: OEPGateRow[] = [];
    Sections: OEPSectionRowVM[] = [];
    NudgeLines: string[] = [];
    readonly NoNudge = OEP_NO_NUDGE;
    CanRemind = false;
    RemindersSent = false;
    TrainingSent = false;
    /** The result of the last gate, send, reopen or export, announced in the aria-live line. */
    Status: OEPStatusLine | null = null;

    private rows: OEPSectionStatusRow[] = [];
    private reloadSub: Subscription | null = null;

    /** A context reload (another write, a gate) re-arms the send buttons (controller ruling, T29 fix round 1). */
    override ngOnInit(): void {
        this.reloadSub = this.Context.Changes$.subscribe(() => this.resetSends());
        super.ngOnInit();
    }

    override ngOnDestroy(): void {
        this.reloadSub?.unsubscribe();
        super.ngOnDestroy();
    }

    private resetSends(): void {
        this.RemindersSent = false;
        this.TrainingSent = false;
    }

    /** Disabled outside the reminder phases, while busy, and after a successful send until the next reload. */
    get RemindersDisabled(): boolean { return !this.CanRemind || this.Busy || this.RemindersSent; }
    get TrainingDisabled(): boolean { return this.Busy || this.TrainingSent; }

    protected async Load(): Promise<void> {
        this.resetSends();
        const season = this.Context.Season;
        if (!season) return;
        const [stats, sections] = await this.Data.Queries([OEP_PROGRAM_STATS_QUERY_ID, OEP_SECTION_STATUS_QUERY_ID]);
        this.rows = sections.map(ParseSectionRow);
        this.SeasonCode = season.Code;
        this.Stats = StatTiles(ParseStatsRow(stats[0]));
        this.Gates = GateRows(season.Phase, season.Timeline);
        this.Sections = SectionRows(this.rows, season.Phase);
        this.NudgeLines = Nudges(season.Phase, this.rows, season.Timeline, new Date());
        this.CanRemind = RemindersEnabled(season.Phase);
    }

    /** "Run" on the next gate: server preview → confirm → RunGate → toast the phase change and mail tallies → reload. */
    async RunGate(row: OEPGateRow): Promise<void> {
        if (this.Busy || !row.Enabled) return;
        const gate = GateByKey(row.Key);
        this.Status = null;
        const preview = await this.guard(() => this.actions.PreviewSend(row.Key));
        if (preview === null) {
            // A refused preview (GATE_ORDER: the phase moved in another tab) — reload so the rows show the real phase.
            await this.Context.Reload();
            await this.Refresh();
            return;
        }
        const yes = await this.confirm.Confirm({
            title: gate.Title, message: PreviewLine(preview), detail: gate.Note,
            type: IsLive(preview) ? 'warning' : 'default', confirmText: 'Run gate', cancelText: 'Cancel',
        });
        if (!yes) return;
        let summary: string | null = null;
        let failed = false;
        const ok = await this.Act(async () => {
            const s = await this.actions.RunGate(row.Key);
            summary = GateToast(s);
            failed = s.Mail.some((t) => t.Failed > 0);
        }, null, (m) => { this.Status = { Text: m, Tone: 'error' }; });
        if (ok && summary !== null) {
            MJNotificationService.Instance.CreateSimpleNotification(summary, failed ? 'warning' : 'success', failed ? 10000 : 5000);
            this.Status = { Text: summary, Tone: failed ? 'error' : 'ok' };
        } else if (!ok) {
            // GATE_ORDER (another tab ran it) / GATE_BUSY: the server message is shown; reload so the rows show the real phase.
            await this.Context.Reload();
            await this.Refresh();
        }
        this.Changed();
    }

    /** "Send reminders" (logic.js:762 reminderLabel, generalized) — preview → confirm → SendReminders. */
    async SendReminders(): Promise<void> {
        if (this.RemindersDisabled) return;
        const tally = await this.previewThenSend('Reminders', 'Send reminders', () => this.actions.SendReminders());
        if (tally !== null) this.RemindersSent = true;
        this.Changed();
    }

    /** "Send training materials" — preview → confirm → SendTraining. */
    async SendTraining(): Promise<void> {
        if (this.TrainingDisabled) return;
        const tally = await this.previewThenSend('Training', 'Send training materials', () => this.actions.SendTraining());
        if (tally !== null) this.TrainingSent = true;
        this.Changed();
    }

    /** "Reopen" on a Submitted slate while Selecting → confirm → ReopenSlate (the slate goes back to Draft). */
    async Reopen(row: OEPSectionRowVM): Promise<void> {
        const id = row.ReopenID;
        if (this.Busy || id === null) return;
        const yes = await this.confirm.Confirm({
            title: `Reopen ${row.Name}'s winners?`,
            message: `The ${row.Name} committee can change its picks and must submit again.`,
            type: 'warning', confirmText: 'Reopen', cancelText: 'Cancel',
        });
        if (!yes) return;
        this.Status = null;
        const ok = await this.Act(() => this.actions.ReopenSlate(id), null, (m) => { this.Status = { Text: m, Tone: 'error' }; });
        if (ok) this.Status = { Text: `${row.Name}'s slate is open again.`, Tone: 'ok' };
        this.Changed();
    }

    /** "Export CSV": the By section table, formula cells neutralized, through ExportEngine.toCSV. */
    async ExportCsv(): Promise<void> {
        this.Status = null;
        try {
            const columns: ExportColumn[] = OEP_SECTION_CSV_COLUMNS.map((c) => ({ name: c, displayName: c }));
            const fileName = `OEP-${this.SeasonCode}-program-status`.replace(/[\\/:*?"<>|]+/g, '-');
            const result = await ExportEngine.toCSV(NeutralizeFormulaCells(SectionCsvRows(this.rows)), { fileName, columns });
            OEPDownloadExport(result, fileName, 'csv', 'Couldn\'t build the CSV.');
        } catch (err) {
            this.Status = { Text: err instanceof Error ? err.message : String(err), Tone: 'error' };
        }
        this.Changed();
    }

    /** Preview → confirm (warning when live) → send → toast the tally. Null when cancelled or refused. */
    private async previewThenSend(purpose: OEPSendPurposeView, title: string, send: () => Promise<OEPTallyView>): Promise<OEPTallyView | null> {
        if (this.Busy) return null;
        this.Status = null;
        const preview = await this.guard(() => this.actions.PreviewSend(purpose));
        if (preview === null) return null;
        const yes = await this.confirm.Confirm({
            title, message: PreviewLine(preview), type: IsLive(preview) ? 'warning' : 'default', confirmText: title, cancelText: 'Cancel',
        });
        if (!yes) return null;
        const tally = await this.guard(send);
        if (tally === null) return null;
        const line = TallyLine(tally);
        this.toastTally(line, [tally]);
        this.Status = { Text: line, Tone: tally.Failed > 0 ? 'error' : 'ok' };
        return tally;
    }

    private toastTally(line: string, tallies: readonly OEPTallyView[]): void {
        const failures = FailureLine(tallies);
        const failed = failures.length > 0;
        MJNotificationService.Instance.CreateSimpleNotification(failed ? `${line}. ${failures}` : line, failed ? 'warning' : 'success', failed ? 10000 : 5000);
    }

    /**
     * Runs one server call with Busy set. A refusal (OEPActionError: GATE_BUSY, GATE_ORDER, NOTHING_TO_REMIND,
     * BAD_SELECTION, FORBIDDEN …) or any failure is toasted and shown in the page status line. The result is null then.
     */
    private async guard<T>(work: () => Promise<T>): Promise<T | null> {
        this.Busy = true;
        this.Changed();
        try {
            return await work();
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            this.Status = { Text: message, Tone: 'error' };
            MJNotificationService.Instance.CreateSimpleNotification(message, 'error', 5000);
            return null;
        } finally {
            this.Busy = false;
            this.Changed();
        }
    }

    /** Button labels (logic.js:762 reminderLabel generalized; the training label pair is implementer copy). */
    get ReminderLabel(): string { return this.RemindersSent ? 'Reminders sent ✓' : 'Send reminders'; }
    get TrainingLabel(): string { return this.TrainingSent ? 'Training materials sent ✓' : 'Send training materials'; }
}
